// The rail (02-DESIGN/03-console/01-the-console.md § the shape): one rail on
// the left holding everything, in three regions — what a person uses, what an
// admin sets under *Administration*, and the person — derived from the role's
// offers, so that what is listed is what the role may do and nothing listed is
// a permission. The workspace sits at the top beside the mark, whose dot is
// the connection's state, and opens the workspace menu (./WorkspaceMenu.tsx);
// the person sits at the bottom with their menu.
// There is no top bar. At a phone's width the rail folds behind the bar's
// *Menu* (./Fold.tsx) and, opened, stands in place of the page; a pick there
// replaces the open entry, so Back from the page picked returns to the page
// the rail was opened over.

import {
  Activity,
  Blocks,
  Box,
  ChevronDown,
  Cpu,
  Database,
  House,
  Inbox,
  KeyRound,
  Lock,
  Network,
  Plug,
  Settings2,
  Shield,
  SlidersHorizontal,
  Users,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router";

import { PAGE_TITLES, tenantPath } from "../address/address.ts";
import type { Who } from "../platform/bus.ts";
import { initialOf } from "../platform/names.ts";
import { useConnection } from "../platform/session.tsx";
import { offered, railFor, type RailEntry } from "../roles/offers.ts";
import { OBSERVABILITY, useInstalled } from "../surfaces/solutions/installed.ts";
import { panelsShown, personPanels } from "../surfaces/solutions/panel.ts";
import { useGrants } from "../surfaces/solutions/sends.ts";
import { RAIL_ID } from "./Fold.tsx";
import { liveOf } from "./Mark.tsx";
import { WorkspaceMenu } from "./WorkspaceMenu.tsx";

const ICONS: Record<RailEntry, LucideIcon> = {
  home: House,
  approvals: Inbox,
  apps: Plug,
  toolkits: Wrench,
  workloads: Box,
  solutions: Blocks,
  people: Users,
  workspaces: Network,
  guardrails: Shield,
  models: Cpu,
  configuration: SlidersHorizontal,
  stores: Database,
  secrets: Lock,
  audit: Activity,
  settings: Settings2,
  "me/activity": Activity,
  "me/credentials": KeyRound,
};

export function Rail({
  slug,
  name,
  who,
  signOut,
  folded = false,
  onLeave,
}: {
  slug: string;
  /** The workspace's name as the shell holds it: the profile's, or what Settings set since. */
  name: string;
  who: Who;
  signOut: () => void;
  /** Whether the rail is open in place of the page at a phone's width: a pick then replaces that entry. */
  folded?: boolean;
  /** What a workspace-menu move does first: at a phone's width, close the open rail. */
  onLeave?: () => void;
}) {
  const { bus, state } = useConnection();
  const live = liveOf(state);
  const role = who.role;
  const rail = railFor(role);
  // The installed solutions' panels and pages, nested under Solutions in the
  // manifest's order (02-DESIGN/14-solutions/03-the-console.md § where a solution appears).
  const installed = useInstalled(bus);
  // The display is the carrier's alone: the server's user is the attestations
  // the auth service signed into the user JWT's name, never a name to show
  // (hq decision 0029).
  const display = who.claims?.display ?? "";
  const [open, setOpen] = useState(false);
  const foot = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onDown = (e: MouseEvent) => {
      if (foot.current !== null && !foot.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // A panel is listed only where something of it is drawn for the person: a
  // role on grant that runs no workload here lists nothing (hq issue 151).
  const admin = offered(role, "solutions:manage");
  const grants = useGrants(bus, admin);
  const shown = (r: (typeof installed)[number]) =>
    r.manifest === undefined ? [] : panelsShown(r.manifest, r.workloads ?? {}, admin, grants);
  // The observability solution's panels are Audit & usage under
  // Administration, and are not listed again here (hq decision 0048). A
  // manifest the console cannot read lists nothing under Solutions, and the
  // rail stands; the Solutions page says what it could not show (§
  // conventions: a reply it cannot draw; hq issue 176).
  const solutions = () => {
    try {
      return listed();
    } catch (err) {
      console.error("Could not list the installed solutions", err);
      return null;
    }
  };
  const listed = () =>
    installed
      .filter((r) => r.name !== OBSERVABILITY)
      .filter((r) => shown(r).length > 0 || r.manifest?.page !== undefined)
      .map((r) => (
        <div key={`solution-${r.name}`}>
          <NavLink
            replace={folded}
            to={tenantPath(slug, "solutions", r.name)}
            end
            className={({ isActive }) => (isActive ? "nav-item sub active" : "nav-item sub")}
          >
            {r.manifest?.display ?? r.name}
          </NavLink>
          {shown(r).map((p) => (
            <NavLink
              replace={folded}
              key={p.name}
              to={tenantPath(slug, "solutions", r.name, p.name)}
              className={({ isActive }) => (isActive ? "nav-item sub active" : "nav-item sub")}
              style={{ paddingLeft: 52 }}
            >
              {p.title}
            </NavLink>
          ))}
          {r.manifest?.page !== undefined && (
            <NavLink
              replace={folded}
              to={tenantPath(slug, "solutions", r.name, "page")}
              className={({ isActive }) => (isActive ? "nav-item sub active" : "nav-item sub")}
              style={{ paddingLeft: 52 }}
            >
              {r.manifest.page.label} ↗
            </NavLink>
          )}
        </div>
      ));

  const entry = (e: RailEntry) => {
    const Icon = ICONS[e];
    if (e === "solutions") {
      return (
        <div key={e}>
          <NavLink
            replace={folded}
            to={tenantPath(slug, e)}
            end
            className={({ isActive }) => (isActive ? "nav-item active" : "nav-item")}
          >
            <Icon strokeWidth={1.6} aria-hidden="true" />
            {PAGE_TITLES[e]}
          </NavLink>
          {solutions()}
        </div>
      );
    }
    return (
      <NavLink
        replace={folded}
        key={e}
        to={e === "home" ? tenantPath(slug) : tenantPath(slug, e)}
        end={e === "home"}
        className={({ isActive }) => (isActive ? "nav-item active" : "nav-item")}
      >
        <Icon strokeWidth={1.6} aria-hidden="true" />
        {e === "home" ? "Home" : PAGE_TITLES[e]}
      </NavLink>
    );
  };

  return (
    <aside className="rail" id={RAIL_ID}>
      <WorkspaceMenu slug={slug} name={name} role={role} live={live} onLeave={onLeave} />
      {live === "reconnecting" && (
        <div className="reconnecting" role="status">
          Reconnecting…
        </div>
      )}
      <div className="rail-space" />
      <nav className="nav" aria-label="Console">
        {rail.use.map(entry)}
        {rail.administration.length > 0 && (
          <>
            <div className="nav-group">Administration</div>
            {rail.administration.map(entry)}
          </>
        )}
      </nav>
      <div className="rail-foot" ref={foot}>
        {open && (
          <div className="menu" role="menu">
            {rail.person.map((p) => (
              <NavLink
                replace={folded}
                key={p}
                role="menuitem"
                to={tenantPath(slug, p)}
                onClick={() => {
                  setOpen(false);
                }}
              >
                {PAGE_TITLES[p]}
              </NavLink>
            ))}
            {/* The installed solutions' panels for the person, Your usage among them (14-solutions/03 § where a solution appears). */}
            {installed.flatMap((r) =>
              r.manifest === undefined
                ? []
                : personPanels(r.manifest, r.workloads ?? {}, admin, grants).map((p) => (
                    <NavLink
                      replace={folded}
                      key={`${r.name}/${p.name}`}
                      role="menuitem"
                      to={tenantPath(slug, "solutions", r.name, p.name)}
                      onClick={() => {
                        setOpen(false);
                      }}
                    >
                      {p.title}
                    </NavLink>
                  )),
            )}
            {rail.person.length > 0 && <hr />}
            <button type="button" role="menuitem" onClick={signOut}>
              Sign out
            </button>
          </div>
        )}
        <button
          type="button"
          className="person"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => {
            setOpen((o) => !o);
          }}
        >
          <span className="avatar" aria-hidden="true">
            {initialOf(display)}
          </span>
          <span className="who">
            <span className="who-name">{display}</span>
            <span className="who-role">{role ?? ""}</span>
          </span>
          <ChevronDown className="chev" strokeWidth={1.6} aria-hidden="true" />
        </button>
      </div>
    </aside>
  );
}
